"use client";

import { useEffect } from "react";
import { requestPersistentStorage } from "@/lib/storage-persistence";

/**
 * Registers the service worker (public/sw.js). Mounted once from the
 * root layout (src/app/layout.tsx); renders nothing.
 *
 * Production-only ON PURPOSE: in dev the service worker would fight
 * HMR (Turbopack rewrites /_next/static/ chunks in place, but the
 * worker's cache-first branch would keep serving the first copy it
 * saw) and pollute the browser cache with dev-build assets that then
 * shadow real production assets on the same origin. NODE_ENV is
 * inlined at build time, so the entire branch is dead code in dev
 * bundles.
 */
export function SwRegister() {
  useEffect(() => {
    // Ask for persistent storage BEFORE the production gate below.
    //
    // Not a service-worker concern, but the same "runs once on load, renders
    // nothing" slot, and it must not be production-only: the offline write
    // queue is testable in dev and its durability should be too.
    //
    // Everything this app keeps offline is in IndexedDB, which is best-effort
    // by default - evictable under pressure, and cleared by iOS for origins it
    // decides are unused after about a week. A home-screen PWA opened three
    // times a week is not obviously "used". This was never requested anywhere,
    // so the durability the write queue advertises has been resting on a
    // default that does not promise it. Fire and forget; Settings reports what
    // the browser actually answered.
    void requestPersistentStorage();

    if (process.env.NODE_ENV !== "production") {
      // NOT JUST "DON'T REGISTER ONE" - ACTIVELY REMOVE ONE.
      //
      // The gate above has been production-only from the start, and the
      // docblock has warned about exactly this since then. It was still not
      // enough, because a gate can only stop a worker being INSTALLED; it
      // cannot remove one that already is. A service worker is per ORIGIN and
      // it outlives the build that registered it, so one `next build && next
      // start` on localhost:3000 - or on the LAN address a phone tests
      // against - installs a worker that then serves every later DEV session
      // on that origin.
      //
      // What that looks like is not "offline behaves oddly". Turbopack
      // rewrites /_next/static/ chunks IN PLACE rather than content-hashing
      // them, and the worker's static branch is cache-first on the stated
      // premise that those URLs are immutable - true of a production build,
      // false in dev. So the worker pins the first copy of every chunk it
      // ever saw and the client JS silently stops updating, while the SERVER
      // render keeps moving. Every client-component edit appears not to land.
      //
      // It cost an afternoon: a body-fat figure rendered on prod and not on
      // dev, from identical code and identical data, and the only visible
      // symptom was a hydration mismatch where the server HTML had the number
      // and the client tree did not.
      if (!("serviceWorker" in navigator)) return;
      void navigator.serviceWorker.getRegistrations().then(async (regs) => {
        if (regs.length === 0) return;
        await Promise.all(regs.map((r) => r.unregister()));
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(
            keys
              .filter((k) => k.startsWith("outrepr-static-"))
              .map((k) => caches.delete(k)),
          );
        }
        // The stale chunks are already running in this page, so unregistering
        // alone changes nothing you can see. Reload once to pick up fresh
        // ones. This cannot loop: the next pass finds no registrations and
        // returns above.
        console.info(
          "[sw-register] Removed a service worker left over from a production build on this origin, cleared its caches and reloaded. Dev was serving you cached JS.",
        );
        window.location.reload();
      });
      return;
    }
    // Older browsers / WebViews without service-worker support just
    // skip registration — the app works identically, minus offline.
    if (!("serviceWorker" in navigator)) return;

    navigator.serviceWorker
      // updateViaCache: "none" — always revalidate sw.js itself
      // against the server on update checks, ignoring the HTTP cache
      // (belt-and-braces alongside the Cache-Control: no-cache header
      // set for /sw.js in next.config.ts). Per the Next.js 16 PWA
      // guide (node_modules/next/dist/docs/01-app/02-guides/
      // progressive-web-apps.md).
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .catch((err) => {
        // Registration failure is non-fatal: the app simply behaves
        // as if no service worker exists (no offline page, no asset
        // cache). Warn quietly for debugging; never surface to the
        // user or the error-capture pipeline — this is expected in
        // e.g. Firefox private windows.
        console.warn("Service worker registration failed:", err);
      });
  }, []);

  return null;
}
